import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SearchIcon, FilterIcon, ShieldAlertIcon, UserPlusIcon, ClipboardPlusIcon, ChevronRightIcon } from 'lucide-react';
import { PageHeader } from '../components/layout/PageHeader';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Avatar } from '../components/ui/Avatar';
import { patients } from '../data/patients';
const statusTone = {
  Admitted: 'brand',
  Outpatient: 'info',
  Discharged: 'neutral',
  Emergency: 'danger'
} as const;
const filters = ['All', 'Admitted', 'Emergency', 'Outpatient'] as const;
export function Patients() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState<(typeof filters)[number]>('All');
  const rows = patients.filter(p => {
    const matchQ = p.name.toLowerCase().includes(q.toLowerCase()) || p.mrn.toLowerCase().includes(q.toLowerCase());
    const matchF = filter === 'All' || p.status === filter;
    return matchQ && matchF;
  });
  return <div className="p-6" data-id="element-3790" data-mp="563vga7zw77c">
      <PageHeader title="Master Patient Index" subtitle="Single universal patient record across every encounter, department and facility" action={<>
            <Button variant="outline" icon={<UserPlusIcon className="h-4 w-4" data-id="element-3793" data-mp="j3ip5r5a7154" />} onClick={() => navigate('/patients/new')} data-id="element-3792" data-mp="j3si9nlmhof1">New Registration</Button>
            <Button icon={<ClipboardPlusIcon className="h-4 w-4" data-id="element-3795" data-mp="o2d7yt390pp8" />} onClick={() => navigate('/reception/encounter')} data-id="element-3794" data-mp="o1k90eaj74hn">New Encounter</Button>
          </>} data-id="element-3791" data-mp="3mkr5g6kd1sxq" />

      <Card className="overflow-hidden" data-id="element-3796" data-mp="e464nd1vu65p">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 p-4" data-id="element-3797" data-mp="e6sa9757qbla">
          <div className="relative flex-1 min-w-[220px]" data-id="element-3798" data-mp="2tuimwdlwx6im">
            <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-3799" data-mp="2yv6izdp00djp" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by name or MRN…" className="w-full rounded-lg border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm outline-none focus:border-brand-500 focus:bg-white focus:ring-2 focus:ring-brand-100" data-id="element-3800" data-mp="15bo67gbxmau9" />
          </div>
          <div className="flex items-center gap-1 rounded-lg bg-slate-100 p-1" data-id="element-3801" data-mp="loq7ac67ju7m">
            {filters.map(f => <button key={f} onClick={() => setFilter(f)} className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${filter === f ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`} data-id="element-3802" data-mp="hlk114j3i315">
                {f}
              </button>)}
          </div>
          <Button variant="outline" size="sm" icon={<FilterIcon className="h-4 w-4" data-id="element-3804" data-mp="220dqfdb17fje" />} data-id="element-3803" data-mp="223i02q79n3hg">Filters</Button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-3805" data-mp="29jwo8poxpdc1">
          <table className="w-full text-sm" data-id="element-3806" data-mp="2ekamjg0qh8wv">
            <thead data-id="element-3807" data-mp="2ej976zcoq09c">
              <tr className="border-b border-slate-100 text-left text-xs font-semibold uppercase tracking-wide text-slate-400" data-id="element-3808" data-mp="2jbsni42rb9hu">
                <th className="px-4 py-2.5 font-semibold" data-id="element-3809" data-mp="2ob596lhw6aiu">Patient</th>
                <th className="px-4 py-2.5 font-semibold" data-id="element-3810" data-mp="1qn4x5u35htxf">MRN</th>
                <th className="px-4 py-2.5 font-semibold hidden md:table-cell" data-id="element-3811" data-mp="1qoogxuo0mn3y">Age / Sex</th>
                <th className="px-4 py-2.5 font-semibold hidden lg:table-cell" data-id="element-3812" data-mp="1vmq7t1ronqpz">Payer</th>
                <th className="px-4 py-2.5 font-semibold" data-id="element-3813" data-mp="20ki03ww8peup">Status</th>
                <th className="px-4 py-2.5 font-semibold hidden lg:table-cell" data-id="element-3814" data-mp="25kxbiiwyoca1">Location</th>
                <th className="px-4 py-2.5" data-id="element-3815" data-mp="3c0q3u1neqw7y"></th>
              </tr>
            </thead>
            <tbody data-id="element-3816" data-mp="3gzj956sjjgx1">
              {rows.map((p, i) => <motion.tr key={p.id} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              delay: i * 0.03
            }} onClick={() => navigate(`/patients/${p.id}`)} className="group cursor-pointer border-b border-slate-50 hover:bg-slate-50/70" data-id="element-3817" data-mp="3gxzqj9jewts3" data-mpi={i}>
                  <td className="px-4 py-3" data-id="element-3818" data-mp="3jjplo2v80fp7">
                    <div className="flex items-center gap-3" data-id="element-3819" data-mp="3jm22udt5mm17">
                      <Avatar name={p.name} src={p.photo} size="md" data-id="element-3820" data-mp="1db13l2g57hu" />
                      <div data-id="element-3821" data-mp="3tieyhh89f9yl">
                        <div className="flex items-center gap-1.5" data-id="element-3822" data-mp="3th3ov0s4v7zk">
                          <span className="font-medium text-slate-900" data-id="element-3823" data-mp="b3w3zkp5ky6k">{p.name}</span>
                          {p.allergies.length > 0 && <ShieldAlertIcon className="h-3.5 w-3.5 text-red-500" aria-label="Has allergies" data-id="element-3824" data-mp="65mbxiyq6dp6" />}
                        </div>
                        <span className="text-xs text-slate-500" data-id="element-3825" data-mp="g41qynkh9wib">{p.bloodGroup} · {p.phone}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600" data-id="element-3826" data-mp="13qnhuysi7mng">{p.mrn}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-600" data-id="element-3827" data-mp="13ob4z01ls2h9">{p.age} · {p.sex}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-slate-600" data-id="element-3828" data-mp="pt8mzejhemo3">{p.payer}</td>
                  <td className="px-4 py-3" data-id="element-3829" data-mp="ursd7r53ykm9">
                    <Badge tone={statusTone[p.status]} data-id="element-3830" data-mp="g0tagwqyeqal">{p.status}</Badge>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell text-xs text-slate-500" data-id="element-3831" data-mp="ks1flgl93phy">{p.location || '—'}</td>
                  <td className="px-4 py-3 text-right" data-id="element-3832" data-mp="71fvjok299ih">
                    <ChevronRightIcon className="h-4 w-4 text-slate-300 group-hover:text-brand-500 inline" data-id="element-3833" data-mp="bzhfebpb9cv0" />
                  </td>
                </motion.tr>)}
            </tbody>
          </table>
          {rows.length === 0 && <div className="py-16 text-center text-sm text-slate-400" data-id="element-3834" data-mp="1zywzzp1tz52">No patients match your search.</div>}
        </div>
      </Card>
    </div>;
}